import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getCoachDisplayName,
  listDirectoryCoaches
} from '../services/coachDirectoryService';
import {
  acceptRelationship,
  declineRelationship,
  endRelationship,
  getCandidateRelationships,
  getRelationshipStatusLabel,
  getRelationshipViewState,
  migrateLegacyRelationships,
  RELATIONSHIP_VIEW_STATE,
  requestCoach
} from '../services/coachRelationshipService';
import { useJobBankRole } from './jobBankRole';

function formatCoachName(relationship = {}) {
  return relationship.coachName || 'Career Coach';
}

export default function CandidateCoachRelationshipsSection({
  currentUser,
  userData
}) {
  const navigate = useNavigate();
  const { role } = useJobBankRole(currentUser);
  const [relationships, setRelationships] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCoachUid, setSavingCoachUid] = useState('');

  const candidateName = getCoachDisplayName(userData || {});
  const candidateEmail = userData?.email || currentUser?.email || '';

  const loadData = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      await migrateLegacyRelationships({ uid: currentUser.uid });

      const [relationshipResults, coachResults] = await Promise.all([
        getCandidateRelationships(currentUser.uid),
        listDirectoryCoaches({
          uid: currentUser.uid,
          userData: userData || {},
          role: role || ''
        })
      ]);

      setRelationships(relationshipResults);
      setCoaches(coachResults);
    } catch (error) {
      console.error('Unable to load career coach relationships:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userData, role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeRelationship = useMemo(() => {
    return relationships.find(rel =>
      rel.active === true &&
      rel.status === 'Active'
    ) || null;
  }, [relationships]);

  const pendingInvitations = useMemo(() => {
    return relationships.filter(rel =>
      getRelationshipViewState(rel) === RELATIONSHIP_VIEW_STATE.COACH_INVITED
    );
  }, [relationships]);

  const pendingRequests = useMemo(() => {
    return relationships.filter(rel =>
      getRelationshipViewState(rel) === RELATIONSHIP_VIEW_STATE.CANDIDATE_REQUESTED
    );
  }, [relationships]);

  const endedRelationships = useMemo(() => {
    return relationships.filter(rel =>
      getRelationshipViewState(rel) === RELATIONSHIP_VIEW_STATE.ENDED
    );
  }, [relationships]);

  const availableCoaches = useMemo(() => {
    const blockedCoachUids = new Set(
      relationships
        .filter(rel => {
          const viewState = getRelationshipViewState(rel);
          return viewState !== RELATIONSHIP_VIEW_STATE.ENDED &&
            viewState !== RELATIONSHIP_VIEW_STATE.DECLINED &&
            viewState !== RELATIONSHIP_VIEW_STATE.NONE;
        })
        .map(rel => rel.coachUid)
    );

    return coaches
      .filter(coach => !blockedCoachUids.has(coach.id))
      .slice(0, 6);
  }, [coaches, relationships]);

  async function handleAccept(relationship) {
    try {
      setSavingCoachUid(relationship.coachUid);
      await acceptRelationship({
        candidateUid: currentUser.uid,
        coachUid: relationship.coachUid,
        candidateName,
        candidateEmail,
        coachName: relationship.coachName,
        coachEmail: relationship.coachEmail
      });
      await loadData();
    } catch (error) {
      console.error('Unable to accept coaching invitation:', error);
      alert(error.message || 'Unable to accept invitation.');
    } finally {
      setSavingCoachUid('');
    }
  }

  async function handleDecline(relationship) {
    if (!window.confirm('Decline this coaching invitation?')) return;

    try {
      setSavingCoachUid(relationship.coachUid);
      await declineRelationship({
        candidateUid: currentUser.uid,
        coachUid: relationship.coachUid
      });
      await loadData();
    } catch (error) {
      console.error('Unable to decline coaching invitation:', error);
      alert('Unable to decline invitation.');
    } finally {
      setSavingCoachUid('');
    }
  }

  async function handleEnd(relationship) {
    if (!window.confirm('End your active coaching relationship?')) return;

    try {
      setSavingCoachUid(relationship.coachUid);
      await endRelationship({
        candidateUid: currentUser.uid,
        coachUid: relationship.coachUid
      });
      await loadData();
    } catch (error) {
      console.error('Unable to end coaching relationship:', error);
      alert(error.message || 'Unable to end relationship.');
    } finally {
      setSavingCoachUid('');
    }
  }

  async function handleRequestCoach(coach) {
    if (activeRelationship) {
      alert('End your current coaching relationship before requesting another coach.');
      return;
    }

    try {
      setSavingCoachUid(coach.id);
      await requestCoach({
        candidateUid: currentUser.uid,
        coachUid: coach.id,
        candidateName,
        candidateEmail,
        coachName: getCoachDisplayName(coach),
        coachEmail: coach.email || ''
      });
      await loadData();
    } catch (error) {
      console.error('Unable to request coach:', error);
      alert(error.message || 'Unable to request coach.');
    } finally {
      setSavingCoachUid('');
    }
  }

  if (loading) {
    return (
      <div className="profileSection candidateCoachRelationships">
        <h3>Career Coach Relationships</h3>
        <p>Loading coaching relationships...</p>
      </div>
    );
  }

  return (
    <div className="profileSection candidateCoachRelationships">
      <h3>Career Coach Relationships</h3>

      <p className="candidateCoachIntro">
        Browse career coaches, respond to invitations, and manage your active
        coaching relationship.
      </p>

      <div className="candidateCoachSubsection">
        <h4>Relationship Status</h4>
        {activeRelationship ? (
          <div className="candidateCoachStatusCard active">
            <strong>{formatCoachName(activeRelationship)}</strong>
            <span>{getRelationshipStatusLabel(RELATIONSHIP_VIEW_STATE.ACTIVE)}</span>
            <div className="candidateCoachActions">
              <button
                type="button"
                onClick={() => navigate(`/careercoaches/${activeRelationship.coachUid}`)}
              >
                View Relationship
              </button>
              <button
                type="button"
                className="secondaryButton"
                disabled={savingCoachUid === activeRelationship.coachUid}
                onClick={() => handleEnd(activeRelationship)}
              >
                End Relationship
              </button>
            </div>
          </div>
        ) : pendingInvitations.length > 0 ? (
          <p>You have pending coaching invitations below.</p>
        ) : pendingRequests.length > 0 ? (
          <p>Your coaching request is awaiting a coach response.</p>
        ) : (
          <p>No active coaching relationship.</p>
        )}
      </div>

      {pendingInvitations.length > 0 && (
        <div className="candidateCoachSubsection">
          <h4>Pending Invitations</h4>
          {pendingInvitations.map(relationship => (
            <div className="candidateCoachStatusCard" key={relationship.id}>
              <strong>{formatCoachName(relationship)}</strong>
              <span>{getRelationshipStatusLabel(RELATIONSHIP_VIEW_STATE.COACH_INVITED)}</span>
              <div className="candidateCoachActions">
                <button
                  type="button"
                  disabled={savingCoachUid === relationship.coachUid}
                  onClick={() => handleAccept(relationship)}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="secondaryButton"
                  disabled={savingCoachUid === relationship.coachUid}
                  onClick={() => handleDecline(relationship)}
                >
                  Decline
                </button>
                <button
                  type="button"
                  className="secondaryButton"
                  onClick={() => navigate(`/careercoaches/${relationship.coachUid}`)}
                >
                  View Coach
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingRequests.length > 0 && (
        <div className="candidateCoachSubsection">
          <h4>Pending Requests</h4>
          {pendingRequests.map(relationship => (
            <div className="candidateCoachStatusCard" key={relationship.id}>
              <strong>{formatCoachName(relationship)}</strong>
              <span>{getRelationshipStatusLabel(RELATIONSHIP_VIEW_STATE.CANDIDATE_REQUESTED)}</span>
              <button
                type="button"
                className="secondaryButton"
                onClick={() => navigate(`/careercoaches/${relationship.coachUid}`)}
              >
                View Coach
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="candidateCoachSubsection">
        <h4>Available Career Coaches</h4>
        {availableCoaches.length === 0 ? (
          <p>No additional coaches are available to request right now.</p>
        ) : (
          <div className="candidateCoachList">
            {availableCoaches.map(coach => (
              <div className="candidateCoachListItem" key={coach.id}>
                <div>
                  <strong>{getCoachDisplayName(coach)}</strong>
                  <p>{coach.coachBio || 'Career coach'}</p>
                </div>
                <div className="candidateCoachActions">
                  <button
                    type="button"
                    disabled={!!activeRelationship || savingCoachUid === coach.id}
                    onClick={() => handleRequestCoach(coach)}
                  >
                    Request Coaching
                  </button>
                  <button
                    type="button"
                    className="secondaryButton"
                    onClick={() => navigate(`/careercoaches/${coach.id}`)}
                  >
                    View Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          className="saveProfileButton"
          onClick={() => navigate('/careercoaches')}
        >
          Browse All Career Coaches
        </button>
      </div>

      {endedRelationships.length > 0 && (
        <div className="candidateCoachSubsection">
          <h4>Ended Relationships</h4>
          {endedRelationships.map(relationship => (
            <div className="candidateCoachStatusCard muted" key={relationship.id}>
              <strong>{formatCoachName(relationship)}</strong>
              <span>{getRelationshipStatusLabel(RELATIONSHIP_VIEW_STATE.ENDED)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
